import React from 'react';
import PropTypes from 'prop-types';
import {Button, Grid} from 'semantic-ui-react';
import {includes as _includes} from 'lodash';

import {hasPrivileges} from '@appComponents/Authorization';
import {CircularProgress, Backdrop} from '@ui';
import Divider from '@ui/Divider';
import {convertToInt} from '@utils/helpers';
import * as CONST from '@constants/variables';
import * as RESOURCES from '@constants/resources';
import stageCategoriesDataPropTypes from '@modules/propertyLicence/utils/stageCategoriesManager';
import StageCategoryTableColumns from '@modules/propertyLicence/components/StageCategoryTableColumns';
import TournamentTableColumns from '@modules/propertyLicence/components/TournamentTableColumns';
import OtherTableColumns from '@modules/propertyLicence/components/OtherTableColumns';

import PropertyLicenceContentForm from '../forms/PropertyLicenceContentForm';

const LARGE_COLUMN = 6;
const SMALL_COLUMN = 2;

const columns = {
    tournament_category: {label: 'Category', showWithPropetyLicenceType: [CONST.PROPERTY_LICENCE_TYPE_SPORTRADAR], width: LARGE_COLUMN},
    first_stage_category: {label: '1st stage category', showWithPropetyLicenceType: [CONST.PROPERTY_LICENCE_TYPE_STAGE_SPORT], width: LARGE_COLUMN},
    second_stage_category: {label: '2nd stage category', showWithPropetyLicenceType: [CONST.PROPERTY_LICENCE_TYPE_STAGE_SPORT], width: LARGE_COLUMN},
    tournament: {label: 'Tournament', showWithPropetyLicenceType: [CONST.PROPERTY_LICENCE_TYPE_SPORTRADAR], width: LARGE_COLUMN},
    content_category_level_2: {label: '2nd level category', showWithPropetyLicenceType: [CONST.PROPERTY_LICENCE_TYPE_OTHER], width: LARGE_COLUMN},
    content_category_level_3: {label: '3rd level category', showWithPropetyLicenceType: [CONST.PROPERTY_LICENCE_TYPE_OTHER], width: LARGE_COLUMN},
    content_tier: {label: 'Tier', showWithPropetyLicenceType: [CONST.PROPERTY_LICENCE_TYPE_SPORTRADAR, CONST.PROPERTY_LICENCE_TYPE_STAGE_SPORT, CONST.PROPERTY_LICENCE_TYPE_OTHER], width: SMALL_COLUMN},
    actions: {label: '', showWithPropetyLicenceType: [CONST.PROPERTY_LICENCE_TYPE_SPORTRADAR, CONST.PROPERTY_LICENCE_TYPE_STAGE_SPORT, CONST.PROPERTY_LICENCE_TYPE_OTHER], width: SMALL_COLUMN},
};

class PropertyLicenceContentTable extends React.Component {
    static defaultProps = {
        addFormData: {},
        contentTiers: [],
        propertyLicence: {},
        propertyLicenceContent: [],
        tournamentCategories: [],
    };

    static propTypes = {
        addFormData: PropTypes.object,
        contentCategoriesSecondLevel: PropTypes.array,
        contentCategoriesThirdLevel: PropTypes.array,
        contentCategoriesThirdLevelLoading: PropTypes.bool,
        contentCategoryThirdLevelValue: PropTypes.string,
        contentTiers: PropTypes.array.isRequired,
        loading: PropTypes.bool,
        onAdd: PropTypes.func,
        onContentCategorySecondLevelChange: PropTypes.func,
        onContentCategoryThirdLevelChange: PropTypes.func,
        onDelete: PropTypes.func,
        onTournamentCategoryChange: PropTypes.func,
        onTournamentChange: PropTypes.func,
        onUpdate: PropTypes.func,
        propertyLicence: PropTypes.object.isRequired,
        propertyLicenceContent: PropTypes.array.isRequired,
        tournamentCategories: PropTypes.array.isRequired,
        tournaments: PropTypes.array,
        tournamentsLoading: PropTypes.bool,
        tournamentValue: PropTypes.string,
        stageCategoriesManager: stageCategoriesDataPropTypes,
    };

    constructor(props) {
        super(props);

        this.state = {
            activeEditableFields: null,
            authorization: {
                read: hasPrivileges({[RESOURCES.PROPERTY_LICENCE_CONTENT]: CONST.SECURITY_PRIVILEGES_READ}),
                create: hasPrivileges({[RESOURCES.PROPERTY_LICENCE_CONTENT]: CONST.SECURITY_PRIVILEGES_CREATE}),
                update: hasPrivileges({[RESOURCES.PROPERTY_LICENCE_CONTENT]: CONST.SECURITY_PRIVILEGES_UPDATE}),
                delete: hasPrivileges({[RESOURCES.PROPERTY_LICENCE_CONTENT]: CONST.SECURITY_PRIVILEGES_DELETE}),
            },
        };
    }

    activateEditableCell = (event) => {
        this.setState({
            activeEditableFields: event.currentTarget.dataset.id,
        });
    };

    deactivateEditableCell = () => {
        this.setState({
            activeEditableFields: null,
        });
    };

    renderHeader = () => {
        const type = this.props.propertyLicence.property_licence_type_id
            ? this.props.propertyLicence.property_licence_type_id
            : 0;

        const cells = Object.keys(columns).map((key) =>
            _includes(columns[key].showWithPropetyLicenceType, convertToInt(type)) ? (
                <Grid.Column key={key} width={columns[key].width}>
                    <strong>{columns[key].label}</strong>
                </Grid.Column>
            ) : false
        );

        return (
            <Grid columns={4}>
                <Grid.Row>{cells}</Grid.Row>
            </Grid>
        );
    };

    renderButtonsInViewMode = (id) => (
        <Button.Group>
            {this.state.authorization.delete && (
                <Button
                    icon='trash'
                    onClick={this.props.onDelete}
                    type='button'
                    data-id={id}
                />
            )}
        </Button.Group>
    );

    renderEditableGrids = () =>
        this.props.propertyLicenceContent.map((data, index) => [
            <Divider key={`divider_${index}`}/>,
            this.renderFormOrEditableGrid(data),
        ]);

    renderFormOrEditableGrid = (data) => {
        if (!this.state.authorization.update) {
            return this.renderEditableGrid(data);
        }

        return this.state.activeEditableFields === data.id
            ? this.renderEditForm(data)
            : this.renderEditableGrid(data);
    };

    renderEditableGrid = (data) => {
        const clickable = this.state.authorization.update;
        const onClick = clickable ? this.activateEditableCell : null;

        let gridColumns = null;

        switch (convertToInt(this.props.propertyLicence.property_licence_type_id)) {
            case CONST.PROPERTY_LICENCE_TYPE_SPORTRADAR:
                gridColumns = <TournamentTableColumns columns={columns} data={data} onClick={onClick}/>;
                break;
            case CONST.PROPERTY_LICENCE_TYPE_STAGE_SPORT:
                gridColumns = <StageCategoryTableColumns columns={columns} data={data} onClick={onClick}/>;
                break;
            case CONST.PROPERTY_LICENCE_TYPE_OTHER:
                gridColumns = <OtherTableColumns columns={columns} data={data} onClick={onClick}/>;
                break;
        }

        return (
            <Grid key={data.id} columns={4} verticalAlign='middle' className={clickable ? '--clickable' : ''}>
                <Grid.Row>
                    {gridColumns}
                    <Grid.Column data-id={data.id} onClick={onClick} width={columns?.content_tier.width}>
                        {data.content_tier?.name}
                    </Grid.Column>

                    <Grid.Column width={columns.actions.width}>
                        {this.renderButtonsInViewMode(data.id)}
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        );
    };

    renderAddForm = () => (
        <PropertyLicenceContentForm
            addFormData={this.props.addFormData}
            contentTiers={this.props.contentTiers}
            formData={{
                content_tier: {id: this.props.propertyLicence.content_tier_id},
            }}
            onAdd={this.props.onAdd}
            onContentCategorySecondLevelChange={this.props.onContentCategorySecondLevelChange}
            onContentCategoryThirdLevelChange={this.props.onContentCategoryThirdLevelChange}
            onTournamentCategoryChange={this.props.onTournamentCategoryChange}
            onTournamentChange={this.props.onTournamentChange}
            tournamentCategories={this.props.tournamentCategories}
            tournaments={this.props.tournaments}
            tournamentsLoading={this.props.tournamentsLoading}
            tournamentValue={this.props.tournamentValue}
            propertyLicence={this.props.propertyLicence}
            propertyLicenceContent={this.props.propertyLicenceContent}
            contentCategoriesSecondLevel={this.props.contentCategoriesSecondLevel}
            contentCategoriesThirdLevel={this.props.contentCategoriesThirdLevel}
            contentCategoriesThirdLevelLoading={this.props.contentCategoriesThirdLevelLoading}
            contentCategoryThirdLevelValue={this.props.contentCategoryThirdLevelValue}
            stageCategoriesManager={this.props.stageCategoriesManager}
            columns={columns}
            authorization={this.state.authorization}
        />
    );

    renderEditForm = (data) => (
        <PropertyLicenceContentForm
            form={`form_${data.id}`}
            contentTiers={this.props.contentTiers}
            formData={{
                property_licence_content: {id: data.id},
                content_tier: {id: data.content_tier.id},
            }}
            onUpdate={this.props.onUpdate}
            staticData={{
                tournament: {name: data.tournament?.name ?? null},
                tournament_category: {name: data.tournament?.tournament_category?.name ?? null},
                content_category_level_3: {name: data.content_category?.name ?? null},
                content_category_level_2: {name: data.content_category?.parent?.name ?? null},
                first_stage_category: {name: data.stage_category?.parent?.name ?? null},
                second_stage_category: {name: data.stage_category?.name ?? null},
            }}
            onDeactivateEditableCell={this.deactivateEditableCell}
            propertyLicence={this.props.propertyLicence}
            columns={columns}
            authorization={this.state.authorization}
        />
    );

    render() {
        if (!this.state.authorization.read) {
            return 'Not Authorized';
        }

        return (
            <div>
                <Backdrop open={this.props.loading} inverted>
                    <CircularProgress />
                </Backdrop>
                {this.renderHeader()}
                {this.state.authorization.create && this.renderAddForm()}
                {this.renderEditableGrids()}
            </div>
        );
    }
}

export default PropertyLicenceContentTable;
